import { StockSourceType, StockTargetType, SystemContext } from '../types';

/**
 * Für eine einzelne (Source -> Target)-Zelle: entscheidet anhand der
 * Funktionsschalter-Konfiguration, ob diese Richtung zulässig ist.
 */
type DirectionCondition = (system: SystemContext) => boolean;

/**
 * Source of Truth für die Erlaubt-Frage: Welche (Source -> Target)-Richtung ist
 * unter welcher Funktionsschalter-Konfiguration zulässig? Vorwärts von der UI
 * gelesen (`allowedTargetsFor`/`allowedSourcesFor`), rückwärts von der
 * Validierung (`disallowedReason`).
 *
 * Zellen ohne Eintrag sind verboten. Jede Bedingung verlangt
 * mindestens einen aktiven Schalter.
 */
const DIRECTION_CONDITIONS: Partial<
  Record<StockSourceType, Partial<Record<StockTargetType, DirectionCondition>>>
> = {
  [StockSourceType.CAR]: {
    // KFZ-Umlagerung: globaler Schalter hier; die per-KFZ-Freigaben
    // (allows_relocation je Fahrzeug) sperrt die UI vor dem Buchen.
    [StockTargetType.CAR]: s => s.carActive && s.carRelocationActive,
    [StockTargetType.BUFFERSTOCK]: s => s.carActive && s.bufferstockActive,
    [StockTargetType.MACHINE]: s => s.carActive,
    // KFZ-Entsorgung ist movement-los (Fahrzeug-`disposal_articles`) und nur in
    // der reinen KFZ-Welt sinnvoll; mit Bufferstock läuft die Verschrottung
    // stattdessen als MachineTrash in den Bufferstock-Disposal.
    [StockTargetType.TRASH_BIN]: s => s.carActive && !s.bufferstockActive,
  },
  [StockSourceType.BUFFERSTOCK]: {
    [StockTargetType.CAR]: s => s.carActive && s.bufferstockActive,
    [StockTargetType.BUFFERSTOCK]: s => s.bufferstockActive,
    [StockTargetType.MACHINE]: s => s.bufferstockActive,
    [StockTargetType.TRASH_BIN]: s => s.bufferstockActive,
    [StockTargetType.CONSUMPTION_BIN]: s => s.bufferstockActive,
  },
  [StockSourceType.MACHINE]: {
    [StockTargetType.CAR]: s => s.carActive,
    // Maschine → Bufferstock ist KEINE eigene Richtung: die Rücknahme bei der
    // Befüllung wird auf Fill-Ebene in die OrderMovement verrechnet
    // (MachineFillBookingService), nicht als zweiphasige NormalMovement gebucht.
    [StockTargetType.BUFFERSTOCK_DISPOSAL]: s => s.bufferstockActive,
  },
};

/** Anzeigenamen für Fehlermeldungen */
const SOURCE_NAMES: Record<StockSourceType, string> = {
  [StockSourceType.CAR]: $localize`KFZ`,
  [StockSourceType.BUFFERSTOCK]: $localize`Bufferstock`,
  [StockSourceType.MACHINE]: $localize`Maschine`,
};

const TARGET_NAMES: Record<StockTargetType, string> = {
  [StockTargetType.CAR]: $localize`KFZ`,
  [StockTargetType.BUFFERSTOCK]: $localize`Bufferstock`,
  [StockTargetType.MACHINE]: $localize`Maschine`,
  [StockTargetType.TRASH_BIN]: $localize`Müll`,
  [StockTargetType.CONSUMPTION_BIN]: $localize`Verbrauch`,
  [StockTargetType.BUFFERSTOCK_DISPOSAL]: $localize`BufferstockDisposal`,
};

/**
 * Ist die Richtung in dieser Konfiguration erlaubt?
 *
 * @param source Quell-Lagertyp
 * @param target Ziel-Lagertyp
 * @param system Aktuelle Funktionsschalter-Konfiguration
 * @returns true, wenn die Richtung unter dieser Konfiguration zulässig ist
 */
export function isDirectionAllowed(
  source: StockSourceType,
  target: StockTargetType,
  system: SystemContext,
): boolean {
  const condition = DIRECTION_CONDITIONS[source]?.[target];
  return condition ? condition(system) : false;
}

/**
 * Alle in dieser Konfiguration erlaubten Ziele für eine Quelle
 * (vorwärts gelesen, z.B. für Button-Sichtbarkeit).
 *
 * @param source Quell-Lagertyp
 * @param system Aktuelle Funktionsschalter-Konfiguration
 * @returns Liste der erlaubten Ziel-Lagertypen
 */
export function allowedTargetsFor(
  source: StockSourceType,
  system: SystemContext,
): StockTargetType[] {
  return Object.values(StockTargetType).filter(target =>
    isDirectionAllowed(source, target, system),
  );
}

/**
 * Alle in dieser Konfiguration erlaubten Quellen für ein Ziel.
 *
 * @param target Ziel-Lagertyp
 * @param system Aktuelle Funktionsschalter-Konfiguration
 * @returns Liste der erlaubten Quell-Lagertypen
 */
export function allowedSourcesFor(
  target: StockTargetType,
  system: SystemContext,
): StockSourceType[] {
  return Object.values(StockSourceType).filter(source =>
    isDirectionAllowed(source, target, system),
  );
}

/**
 * Begründung, warum die Richtung nicht erlaubt ist - `null` wenn erlaubt.
 * Liefert die spezifischste zutreffende Ursache (Schalter vor Statik).
 *
 * @param source Quell-Lagertyp
 * @param target Ziel-Lagertyp
 * @param system Aktuelle Funktionsschalter-Konfiguration
 * @returns Lokalisierte Begründung oder null, wenn die Richtung erlaubt ist
 */
export function disallowedReason(
  source: StockSourceType,
  target: StockTargetType,
  system: SystemContext,
): string | null {
  if (isDirectionAllowed(source, target, system)) return null;

  const involvesCar =
    source === StockSourceType.CAR || target === StockTargetType.CAR;
  const involvesBufferstock =
    source === StockSourceType.BUFFERSTOCK ||
    target === StockTargetType.BUFFERSTOCK ||
    target === StockTargetType.BUFFERSTOCK_DISPOSAL;

  if (involvesCar && !system.carActive) {
    return $localize`KFZ-Schalter aus`;
  }
  if (involvesBufferstock && !system.bufferstockActive) {
    return $localize`Bufferstock-Schalter aus`;
  }
  if (
    source === StockSourceType.CAR &&
    target === StockTargetType.CAR &&
    !system.carRelocationActive
  ) {
    return $localize`Schalter KFZ-Umlagerung nicht aktiv`;
  }
  return $localize`Richtung ${SOURCE_NAMES[source]} → ${TARGET_NAMES[target]} ist nicht erlaubt`;
}
